// BUG-518: discount codes (rabatkoder). A code takes X % or X kr off the goods, never the shipping.
// The webshop API decides everything (validation, amounts, limits); this module only shapes what the
// shopper typed, reads the API's refusal codes and adds up what the basket and checkout show.
// Contract: helinhair-webshop `POST /api/webshop-public/discount-codes/validation`.

import { ApiError } from './shopApi'
import { formatKr } from './format'
import type { CreateOrderLineRequest } from './types'
import type { Lang } from '../../config/locales'

/** The longest code the API takes (CreateOrderRequest.discountCode). */
export const MAX_CODE_LENGTH = 40

/** The refusal codes the API answers with (400, `code`), in the order they are checked there. */
export const DISCOUNT_ERROR_CODES = [
  'DISCOUNT_CODE_UNKNOWN',
  'DISCOUNT_CODE_INACTIVE',
  'DISCOUNT_CODE_NOT_STARTED',
  'DISCOUNT_CODE_EXPIRED',
  'DISCOUNT_CODE_USED_UP',
  'DISCOUNT_CODE_MINIMUM_NOT_MET',
] as const

export type DiscountErrorCode = (typeof DISCOUNT_ERROR_CODES)[number]

export interface DiscountValidationRequest {
  code: string
  lines: CreateOrderLineRequest[]
  locale: string
}

/** The API's answer for a code that applies to these lines. Amounts are kroner incl. VAT. */
export interface DiscountValidation {
  code: string
  type: 'PERCENT' | 'AMOUNT'
  percentOff: number | null
  amountOff: number | null
  /** The goods before the discount. */
  subtotal: number
  discountAmount: number
  goodsAfterDiscount: number
  currency: string
}

/** Why the API would not take the code; the minimum only for MINIMUM_NOT_MET. */
export interface DiscountRefusal {
  code: DiscountErrorCode
  minimumOrderAmount: number | null
}

/** "  sommer10 " becomes "SOMMER10": the API is case-insensitive and trims, so the shop shows it the same way. */
export function normaliseCode(raw: string): string {
  return raw.trim().toUpperCase()
}

/**
 * True for something that can be a code at all: 3 to 40 of A-Z, 0-9, "-" and "_" (the admin's rule
 * for creating one). Anything else is answered "does not exist" without asking the API.
 */
export function isWellFormedCode(code: string): boolean {
  return /^[A-Z0-9_-]{3,40}$/.test(code)
}

const isErrorCode = (value: unknown): value is DiscountErrorCode =>
  typeof value === 'string' && (DISCOUNT_ERROR_CODES as readonly string[]).includes(value)

/**
 * The refusal inside an API error from validation, create order or the checkout session, or null
 * for any other error (network, rate limit, stock) that is not about the code.
 */
export function discountRefusalOf(err: unknown): DiscountRefusal | null {
  if (!(err instanceof ApiError)) return null
  const p = err.problem as { code?: unknown; minimumOrderAmount?: unknown } | null
  if (!p || !isErrorCode(p.code)) return null
  const min = p.minimumOrderAmount
  return { code: p.code, minimumOrderAmount: typeof min === 'number' && Number.isFinite(min) ? min : null }
}

/** The translation key per refusal: the shop's own words, never the API's text. */
export const REFUSAL_KEYS: Record<DiscountErrorCode, string> = {
  DISCOUNT_CODE_UNKNOWN: 'discount.errors.unknown',
  DISCOUNT_CODE_INACTIVE: 'discount.errors.inactive',
  DISCOUNT_CODE_NOT_STARTED: 'discount.errors.notStarted',
  DISCOUNT_CODE_EXPIRED: 'discount.errors.expired',
  DISCOUNT_CODE_USED_UP: 'discount.errors.usedUp',
  DISCOUNT_CODE_MINIMUM_NOT_MET: 'discount.errors.minimumNotMet',
}

/** Key and values for a refusal's message; the minimum is formatted by the caller ("300 kr"). */
export function refusalMessage(
  refusal: DiscountRefusal,
  formatAmount: (amount: number) => string,
): { key: string; values: Record<string, string> } {
  if (refusal.code === 'DISCOUNT_CODE_MINIMUM_NOT_MET') {
    return refusal.minimumOrderAmount === null
      ? { key: 'discount.errors.minimumNotMetNoAmount', values: {} }
      : { key: REFUSAL_KEYS[refusal.code], values: { amount: formatAmount(refusal.minimumOrderAmount) } }
  }
  return { key: REFUSAL_KEYS[refusal.code], values: {} }
}

/** The basket lines as the API takes them, plus a key that changes when any of them changes. */
export function requestLines(
  lines: ReadonlyArray<{ productId: string; variantId: string | null; quantity: number }>,
): CreateOrderLineRequest[] {
  return lines.map((l) => ({ productId: l.productId, variantId: l.variantId, quantity: l.quantity }))
}

export function linesSignature(lines: ReadonlyArray<CreateOrderLineRequest>): string {
  return lines.map((l) => `${l.productId}:${l.variantId ?? ''}:${l.quantity}`).join('|')
}

const round2 = (n: number) => Math.round(n * 100) / 100

export interface DiscountTotals {
  /** What comes off the goods (0 without a code that applies). Never more than the goods. */
  discount: number
  goodsAfterDiscount: number
  /** Goods after the discount plus shipping: what the shopper pays. */
  total: number
}

/**
 * The amounts the summary shows. The discount is the API's figure for these lines; shipping is
 * added after it, since a code never takes anything off the shipping.
 */
export function discountTotals(subtotal: number, shipping: number, applied: Pick<DiscountValidation, 'discountAmount'> | null): DiscountTotals {
  const discount = applied ? round2(Math.min(Math.max(0, applied.discountAmount), subtotal)) : 0
  const goodsAfterDiscount = round2(subtotal - discount)
  return { discount, goodsAfterDiscount, total: round2(goodsAfterDiscount + shipping) }
}

/**
 * What the shipping bands are matched against (BUG-524, owner 2026-10-20): the goods after the discount,
 * as the server prices shipping. So a code that takes the goods under "free shipping above X" brings the
 * shipping fee back, here and on the order alike.
 */
export function shippingGoods(subtotal: number, applied: Pick<DiscountValidation, 'discountAmount'> | null): number {
  return discountTotals(subtotal, 0, applied).goodsAfterDiscount
}

/** The discount as the totals show it: "-50 kr" (a plain hyphen-minus, the shop's money format). */
export function minusKr(amount: number, locale: Lang): string {
  return `-${formatKr(amount, locale)}`
}
